
import i18next from 'i18next';
import _ from 'lodash';
import type { Dispatch } from 'redux';

import { createBreakoutRoomsEvent } from '../analytics/AnalyticsEvents';
import { sendAnalytics } from '../analytics/functions';
import {
    conferenceLeft,
    conferenceWillLeave,
    createConference,
    setPassword
} from '../base/conference/actions';
import { CONFERENCE_LEAVE_REASONS } from '../base/conference/constants';
import { getCurrentConference } from '../base/conference/functions';
import { setAudioMuted, setVideoMuted } from '../base/media/actions';
import { MEDIA_TYPE } from '../base/media/constants';
import { setIsSwitchingBreakoutRoom, setIsSwitchingToMainRoom } from '../base/mtApi';
import { fakeParticipantLeft } from '../base/mtApi/actions';
import { FakeParticipant } from '../base/mtApi/constants';
import { getParticipants } from '../base/participants/functions';
import { createDesiredLocalTracks, destroyLocalDesktopTrackIfExists } from '../base/tracks/actions';
import {
    getLocalTracks,
    isLocalTrackMuted,
    isScreenVideoShared
} from '../base/tracks/functions';
import { clearNotifications, showNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { isAudioOnlySharing } from '../screen-share/functions';
import { setRoomSwitchTarget } from '../webinar/actions';

import { SET_PERSISTENT_BREAKOUT_AUTO_ASSIGN, _RESET_BREAKOUT_ROOMS, _UPDATE_ROOM_COUNTER } from './actionTypes';
import { FEATURE_KEY } from './constants';
import {
    getBreakoutRooms,
    getMainRoom,
    getRoomByJid,
    isBreakoutRoomCreationAllowed,
    stopScreenSharingWhileMovingBreakoutRoom
} from './functions';
import logger from './logger';


const PASSWORD_RETRY_DELAY_MS = 500;
const PASSWORD_RETRY_MAX_ATTEMPTS = 10;


/**
 * Applies the room passcode once the destination conference is available.
 *
 * @param {Dispatch<any>} dispatch - The redux dispatch function.
 * @param {Function} getState - The redux state getter.
 * @param {string} targetRoomId - The room we are switching to.
 * @param {Object} options - Retry and password options.
 * @returns {void}
 */
function _applyRoomPasswordWhenReady(
        dispatch: Dispatch<any>,
        getState: Function,
        targetRoomId: string,
        options: {
        attemptsLeft?: number,
        password: string
    }) {
    const {
        attemptsLeft = PASSWORD_RETRY_MAX_ATTEMPTS,
        password
    } = options;

    if (!password || attemptsLeft <= 0) {
        if (attemptsLeft <= 0) {
            logger.warn(`Timed out waiting to apply password for room ${targetRoomId}`);
        }

        return;
    }

    const conference = getCurrentConference(getState);
    const conferenceName = conference?.getName?.() || conference?.roomName;

    if (conference && conferenceName === targetRoomId) {
        dispatch(setPassword(conference, conference.join, password));

        return;
    }

    setTimeout(() => _applyRoomPasswordWhenReady(
        dispatch,
        getState,
        targetRoomId,
        {
            attemptsLeft: attemptsLeft - 1,
            password
        }
    ), PASSWORD_RETRY_DELAY_MS);
}


// import BreakoutRoomNamePrompt from './components/web/BreakoutRoomNamePrompt';

declare var APP: Object;


/**
 * Action to create a breakout room.
 *
 * @param {string} name - Name / subject for the breakout room.
 * @returns {Function}
 */
export function createBreakoutRoom(name?: string) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();

        // The button and API can fire again before the server confirms the
        // previous room; the limit must hold for those too.
        if (!isBreakoutRoomCreationAllowed(state)) {
            return;
        }

        let { roomCounter } = state[FEATURE_KEY];
        const subject = name || i18next.t('breakoutRooms.defaultName', { index: ++roomCounter });

        sendAnalytics(createBreakoutRoomsEvent('create'));
        dispatch({
            type: _UPDATE_ROOM_COUNTER,
            roomCounter
        });

        getCurrentConference(state)?.getBreakoutRooms()
            ?.createBreakoutRoom(subject);
    };
}

/**
 * Action to close a room and send participants to the main room.
 *
 * @param {string} roomId - The id of the room to close.
 * @returns {Function}
 */
export function closeBreakoutRoom(roomId: string) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const rooms = getBreakoutRooms(getState);
        const room = rooms[roomId];
        const mainRoom = getMainRoom(getState);

        sendAnalytics(createBreakoutRoomsEvent('close'));

        if (room && mainRoom) {
            Object.values(room.participants).forEach(p => {
                dispatch(sendParticipantToRoom(p.jid, mainRoom.id));
            });
        }
    };
}

/**
 * Action to rename a breakout room.
 *
 * @param {string} breakoutRoomJid - The jid of the breakout room to rename.
 * @param {string} name - New name / subject for the breakout room.
 * @returns {Function}
 */
export function renameBreakoutRoom(breakoutRoomJid: string, name = '') {
    return (_dispatch: Dispatch<any>, getState: Function) => {
        const trimmedName = name.trim();

        if (trimmedName.length !== 0) {
            sendAnalytics(createBreakoutRoomsEvent('rename'));
            getCurrentConference(getState)?.getBreakoutRooms()
                ?.renameBreakoutRoom(breakoutRoomJid, trimmedName);
        }
    };
}

/**
 * Action to remove a breakout room.
 *
 * @param {string} breakoutRoomJid - The jid of the breakout room to remove.
 * @returns {Function}
 */
export function removeBreakoutRoom(breakoutRoomJid: string): Function {
    return (dispatch: Dispatch<any>, getState: Function) => {
        sendAnalytics(createBreakoutRoomsEvent('remove'));
        const room = getRoomByJid(getState, breakoutRoomJid);

        if (!room) {
            // The room is already gone - a double tap, or another moderator removed it first. That is the
            // outcome this action was asking for, so it is not a failure.
            logger.warn('The room to remove was not found; it is already removed.');

            return;
        }

        if (Object.keys(room.participants).length > 0) {
            dispatch(closeBreakoutRoom(room.id));
        }
        getCurrentConference(getState)?.getBreakoutRooms()
            ?.removeBreakoutRoom(breakoutRoomJid);
    };
}

/**
 * Action to auto-assign the participants to breakout rooms.
 *
 * @returns {Function}
 */
export function autoAssignToBreakoutRooms() {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const rooms = getBreakoutRooms(getState);
        const breakoutRooms = _.filter(rooms, room => !room.isMainRoom);

        if (breakoutRooms && breakoutRooms.length > 0) {
            sendAnalytics(createBreakoutRoomsEvent('auto.assign'));
            const participantIds = getParticipants(getState)
                .filter(p => !p.local)
                .map(p => p.id);
            const length = Math.ceil(participantIds.length / breakoutRooms.length) || participantIds.length;

            _.chunk(_.shuffle(participantIds), length).forEach((group, index) =>
                group.forEach(participantId => {
                    dispatch(sendParticipantToRoom(participantId, breakoutRooms[index % breakoutRooms.length].id));
                })
            );
        }
    };
}

/**
 * Action to set the persistent auto-assign setting.
 *
 * @param {boolean} persistent - Whether auto-assign should be persistent.
 * @returns {{
 *     type: SET_PERSISTENT_BREAKOUT_AUTO_ASSIGN,
 *     persistent: boolean
 * }}
 */
export function setPersistentBreakoutAutoAssign(persistent: boolean) {
    return {
        type: SET_PERSISTENT_BREAKOUT_AUTO_ASSIGN,
        persistent
    };
}


/**
 * Action to send a participant to a room.
 *
 * @param {string} participantId - The participant id.
 * @param {string} roomId - The room id.
 * @returns {Function}
 */
export function sendParticipantToRoom(participantId: string, roomId: string) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const rooms = getBreakoutRooms(getState);
        const room = rooms[roomId];

        if (!room) {
            logger.warn(`Invalid room: ${roomId}`);

            return;
        }

        // Get the full JID of the participant. We could be getting the endpoint ID or
        // a participant JID. We want to find the connection JID.
        const participantJid = _findParticipantJid(getState, participantId);

        if (!participantJid) {
            logger.warn(`Could not find participant ${participantId}`);

            return;
        }

        getCurrentConference(getState)?.getBreakoutRooms()
            ?.sendParticipantToRoom(participantJid, room.jid);
    };
}

/**
 * Action to move to a room.
 *
 * Somebody a moderator put in a breakout room stays there until a moderator
 * moves them again: the server refuses any other join of theirs, so unless the
 * move was asked for by a moderator (the server-relayed move-to-room event) a
 * non-stage participant's own move out of a breakout room is not attempted.
 *
 * @param {string} roomId - The room id to move to. If omitted move to the main room.
 * @param {Object} [options] - Options.
 * @param {boolean} [options.byModerator] - Whether a moderator asked for this
 * move rather than the participant themselves.
 * @returns {Function}
 */
export function moveToRoom(roomId?: string, { byModerator: _byModerator = false }: Object = {}) {
    return async (dispatch: Dispatch<any>, getState: Function) => {

        const state = getState();
        const isSharing = isScreenVideoShared(state) || isAudioOnlySharing(state);
        const currentConference = getCurrentConference(getState);

        if (isSharing) {
            const localScreenShare = state['features/base/mtApi']?.localScreenShare;

            if (localScreenShare) {
                dispatch(fakeParticipantLeft(localScreenShare.id, currentConference, {
                    fakeParticipant: FakeParticipant.LocalScreenShare
                }));
            }

            try {
                // Wait for screen sharing to be completely stopped before switching rooms.
                // Without await, the state update may not complete before the room transition,
                // leaving the UI icon showing "sharing" even though the stream is stopped.
                const shouldAskStopSharing = await stopScreenSharingWhileMovingBreakoutRoom(dispatch);

                if (shouldAskStopSharing === false) {
                    return;
                }
            } catch (e) {
                logger.error('Error stopping screen sharing when moving to breakout room:', e);
            }
        }

        const rooms = getBreakoutRooms(getState) || {};
        const mainRoom = getMainRoom(getState);
        const mainRoomJid = mainRoom?.jid || currentConference?.getBreakoutRooms()?.getMainRoomJid?.();
        const mainRoomName = mainRoom?.name || mainRoom?.id || state['features/base/conference'].room;
        const mainRoomIdentifier = mainRoom?.id || mainRoomJid || mainRoomName;

        let _roomId: string | undefined | String = roomId || mainRoomIdentifier;

        const rawRoomIdStr = _roomId?.toString();
        const rawRoomIdNoDomain = rawRoomIdStr?.split('@')[0];
        const mainRoomJidNoDomain = mainRoomJid?.toString()?.split('@')[0];
        const mainRoomNameNoDomain = mainRoomName?.toString()?.split('@')[0];
        const mainRoomIdNoDomain = mainRoom?.id?.toString()?.split('@')[0];

        // Determine if we're going to the main room before any transformations
        let targetRoom = rooms[rawRoomIdStr ?? '']
            || (rawRoomIdNoDomain ? rooms[rawRoomIdNoDomain] : undefined)
            || Object.values(rooms).find((r: any) =>
                r.id === rawRoomIdStr
                || r.id === rawRoomIdNoDomain
                || r.jid === rawRoomIdStr
                || r.jid?.split('@')[0] === rawRoomIdNoDomain
            );

        const goToMainRoom = !roomId || Boolean(
            targetRoom?.isMainRoom
            || (mainRoom?.id && (rawRoomIdStr === mainRoom.id.toString() || rawRoomIdNoDomain === mainRoomIdNoDomain))
            || (mainRoomJid && (rawRoomIdStr === mainRoomJid.toString() || rawRoomIdNoDomain === mainRoomJidNoDomain))
            || (mainRoomName && (rawRoomIdStr === mainRoomName.toString() || rawRoomIdNoDomain === mainRoomNameNoDomain))
        );

        if (goToMainRoom && !targetRoom && mainRoom) {
            targetRoom = mainRoom;
        }

        // Set the appropriate flag based on whether going to main room or breakout room
        // This is necessary to preserve virtual backgrounds during room transitions
        if (goToMainRoom) {
            dispatch(setIsSwitchingToMainRoom(true));
            dispatch(setIsSwitchingBreakoutRoom(false));
        } else {
            dispatch(setIsSwitchingBreakoutRoom(true));
            dispatch(setIsSwitchingToMainRoom(false));
        }

        // Tracked like a webinar room switch, so a join the server refuses
        // (the breakout hold, a room that vanished) is unwound by the webinar
        // middleware and we land back where we came from instead of on the
        // switching screen forever. The room we are leaving is recorded the
        // way joinRoom takes it: a breakout room is not on the main MUC, so
        // its name has to carry its domain, exactly as _roomId does below.
        dispatch(setRoomSwitchTarget(
            goToMainRoom ? 'breakout-main' : 'breakout',
            _getJoinableRoomName(currentConference)));

        // Check if we got a full JID.
        if (_roomId && _roomId?.indexOf('@') !== -1) {
            const [ id, ...domainParts ] = _roomId.split('@');

            // On mobile we first store the room and the connection is created
            // later, so let's attach the domain to the room String object as
            // a little hack.

            // eslint-disable-next-line no-new-wrappers
            _roomId = new String(id);

            _roomId.domain = domainParts.join('@');


        }

        const roomIdStr = _roomId?.toString();

        if (!targetRoom) {
            targetRoom = rooms[roomIdStr ?? ''] || (goToMainRoom ? mainRoom : undefined);
        }

        if (!targetRoom && !goToMainRoom) {
            logger.warn(`Unknown room: ${_roomId}`);

            return;
        }

        dispatch({
            type: _RESET_BREAKOUT_ROOMS
        });

        const preregData = getState()['features/base/mtApi']?.pregData ?? {};

        // Fall back to the password typed into the prompt: it is cleared on leave.
        const passcode = preregData?.passcode || getState()['features/base/conference'].password;

        if (navigator.product === 'ReactNative') {
            const conference = getCurrentConference(getState);
            const { audio, video } = getState()['features/base/media'];

            dispatch(conferenceWillLeave(conference));

            try {
                await conference?.leave(CONFERENCE_LEAVE_REASONS.SWITCH_ROOM);
            } catch (error) {
                logger.warn('MHConference.leave() rejected with:', error);

                dispatch(conferenceLeft(conference));
            }

            dispatch(clearNotifications());
            await dispatch(destroyLocalDesktopTrackIfExists());
            dispatch(createConference(_roomId));
            dispatch(setAudioMuted(audio.muted));
            dispatch(setVideoMuted(Boolean(video.muted)));
            dispatch(createDesiredLocalTracks());

            if (passcode) {
                _applyRoomPasswordWhenReady(dispatch, getState, roomIdStr ?? '', { password: passcode });
            }
        } else {
            const localTracks = getLocalTracks(getState()['features/base/tracks']);
            const isAudioMuted = isLocalTrackMuted(localTracks, MEDIA_TYPE.AUDIO);
            const isVideoMuted = isLocalTrackMuted(localTracks, MEDIA_TYPE.VIDEO);

            try {
                // all places we fire notifyConferenceLeft we pass the room name from APP.conference
                await APP.conference.leaveRoom(false /* doDisconnect */, CONFERENCE_LEAVE_REASONS.SWITCH_ROOM).then(
                    () => APP.API.notifyConferenceLeft(APP.conference.roomName));
            } catch (error) {
                logger.warn('APP.conference.leaveRoom() rejected with:', error);

                // TODO: revisit why we don't dispatch CONFERENCE_LEFT here.
            }

            await dispatch(destroyLocalDesktopTrackIfExists());

            APP.conference.joinRoom(_roomId, {
                password: passcode,
                startWithAudioMuted: isAudioMuted,
                startWithVideoMuted: isVideoMuted
            });

            if (passcode) {
                _applyRoomPasswordWhenReady(dispatch, getState, roomIdStr ?? '', { password: passcode });
            }
        }

        if (goToMainRoom) {
            dispatch(showNotification({
                titleKey: 'breakoutRooms.notifications.joinedTitle',
                descriptionKey: 'breakoutRooms.notifications.joinedMainRoom',
                concatText: true,
                maxLines: 2
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));

        } else {
            dispatch(showNotification({
                titleKey: 'breakoutRooms.notifications.joinedTitle',
                descriptionKey: 'breakoutRooms.notifications.joined',
                descriptionArguments: { name: targetRoom?.name || '' },
                concatText: true,
                maxLines: 2
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        }
    };
}

/**
 * The name of a conference's room in the form {@code APP.conference.joinRoom}
 * takes: the node, carrying the MUC domain when the room is not on the main
 * MUC (a breakout room). Falls back to the plain name.
 *
 * @param {Object} conference - The conference.
 * @returns {string|String|undefined}
 */
function _getJoinableRoomName(conference: Object) {
    const roomJid = conference?.room?.roomjid;

    if (typeof roomJid === 'string' && roomJid.includes('@')) {
        const [ node, ...domainParts ] = roomJid.split('@');

        // eslint-disable-next-line no-new-wrappers
        const name = new String(node);

        name.domain = domainParts.join('@');

        return name;
    }

    return conference?.getName?.();
}

/**
 * Finds a participant's connection JID given its ID.
 *
 * @param {Function} getState - The redux store state getter.
 * @param {string} participantId - ID of the given participant.
 * @returns {string|undefined} - The participant connection JID if found.
 */
function _findParticipantJid(getState: Function, participantId: string) {
    const conference = getCurrentConference(getState);

    if (!conference) {
        return;
    }

    // Get the full JID of the participant. We could be getting the endpoint ID or
    // a participant JID. We want to find the connection JID.
    let _participantId = participantId;
    let participantJid;

    if (typeof participantId === 'undefined' || typeof participantId !== 'string' || !participantId.includes('@')) {
        const p = conference.getParticipantById(participantId);

        _participantId = p?.getJid(); // This will be the room JID.
    }

    if (_participantId) {
        const rooms = getBreakoutRooms(getState);

        for (const room of Object.values(rooms)) {
            const participants = room.participants || {};
            const p = participants[_participantId]
                || Object.values(participants).find(item => item.jid === _participantId);

            if (p) {
                participantJid = p.jid;
                break;
            }
        }

        if (!participantJid && _participantId.includes('@')) {
            participantJid = _participantId;
        }
    }

    return participantJid;
}


// export const hideRenameDialog =() => hideDialog(BreakoutRoomNamePrompt);
